import { useEffect, useState, type FormEvent } from "react";
import type { AreaPractica, Permiso, Rol, Usuario } from "../../../shared/types";
import { useAuth } from "../../auth/AuthContext";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { EncabezadoPagina } from "../../components/EncabezadoPagina";
import { Campo, CampoBusqueda, Input, Select } from "../../components/Formulario";
import { Insignia } from "../../components/Insignia";
import { Modal } from "../../components/Modal";
import { SelectorMultiple } from "../../components/SelectorMultiple";
import { Tabla, type ColumnaTabla } from "../../components/Tabla";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import {
  CATALOGO_PERMISOS,
  ETIQUETAS_PERMISOS,
  GRUPOS_PERMISOS,
  PERMISOS_POR_ROL,
} from "../../lib/permisos";

const ROLES: Rol[] = [
  "ADMINISTRADOR",
  "ABOGADO",
  "PROCURADOR",
  "ADMINISTRATIVO",
  "RECEPCION",
];
const ETIQUETA_ROL: Record<Rol, string> = {
  ADMINISTRADOR: "Administrador",
  ABOGADO: "Abogado",
  PROCURADOR: "Procurador",
  ADMINISTRATIVO: "Administrativo",
  RECEPCION: "Recepción",
};

type EstadoOverride = "default" | "conceder" | "denegar";

const valoresIniciales = {
  email: "",
  password: "",
  nombre: "",
  apellido: "",
  telefono: "",
  matricula: "",
  cargo: "",
  rol: "ABOGADO" as Rol,
};

export function UsuariosPage() {
  const { usuario: usuarioActual, permisos } = useAuth();
  const { mostrar } = useToast();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");

  const [modalAbierto, setModalAbierto] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);
  const [valores, setValores] = useState(valoresIniciales);
  const [guardando, setGuardando] = useState(false);

  const [aCambiarEstado, setACambiarEstado] = useState<Usuario | null>(null);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const [aCambiarRol, setACambiarRol] = useState<Usuario | null>(null);
  const [rolNuevo, setRolNuevo] = useState<Rol>("ABOGADO");
  const [guardandoRol, setGuardandoRol] = useState(false);

  const [aRestablecer, setARestablecer] = useState<Usuario | null>(null);
  const [passwordManual, setPasswordManual] = useState("");
  const [restableciendo, setRestableciendo] = useState(false);
  const [passwordTemporal, setPasswordTemporal] = useState<string | null>(
    null,
  );

  const [gestionandoPermisos, setGestionandoPermisos] =
    useState<Usuario | null>(null);
  const [estadosPermisos, setEstadosPermisos] = useState<
    Record<Permiso, EstadoOverride>
  >({} as Record<Permiso, EstadoOverride>);
  const [cargandoPermisos, setCargandoPermisos] = useState(false);
  const [guardandoPermisos, setGuardandoPermisos] = useState(false);

  const [areasDisponibles, setAreasDisponibles] = useState<AreaPractica[]>([]);
  const [gestionandoAreas, setGestionandoAreas] = useState<Usuario | null>(null);
  const [areasSeleccionadas, setAreasSeleccionadas] = useState<Set<string>>(new Set());
  const [cargandoAreas, setCargandoAreas] = useState(false);
  const [guardandoAreas, setGuardandoAreas] = useState(false);

  useEffect(() => {
    api
      .get<{ items: AreaPractica[] }>("/catalogos-estudio/areas-practica")
      .then((d) => setAreasDisponibles(d.items.filter((a) => a.activo)))
      .catch(() => undefined);
  }, []);

  function cargar() {
    setCargando(true);
    const params = new URLSearchParams();
    if (busqueda.trim()) params.set("busqueda", busqueda.trim());
    if (filtroRol) params.set("rol", filtroRol);
    if (filtroEstado) params.set("estado", filtroEstado);
    const query = params.toString();
    api
      .get<{ items: Usuario[] }>(`/usuarios${query ? `?${query}` : ""}`)
      .then((d) => setUsuarios(d.items))
      .catch(() => mostrar("No se pudieron cargar los usuarios.", "error"))
      .finally(() => setCargando(false));
  }

  useEffect(cargar, [busqueda, filtroRol, filtroEstado]); // eslint-disable-line react-hooks/exhaustive-deps

  function abrirCrear() {
    setEditando(null);
    setValores(valoresIniciales);
    setModalAbierto(true);
  }

  function abrirEditar(u: Usuario) {
    setEditando(u);
    setValores({
      email: u.email,
      password: "",
      nombre: u.nombre ?? "",
      apellido: u.apellido ?? "",
      telefono: u.telefono ?? "",
      matricula: u.matricula ?? "",
      cargo: u.cargo ?? "",
      rol: u.rol,
    });
    setModalAbierto(true);
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando) {
        await api.put(`/usuarios/${editando.id}`, {
          nombre: valores.nombre,
          apellido: valores.apellido,
          telefono: valores.telefono || undefined,
          matricula: valores.matricula || undefined,
          cargo: valores.cargo || undefined,
        });
        mostrar("Usuario actualizado.", "exito");
      } else {
        await api.post("/usuarios", {
          email: valores.email,
          password: valores.password,
          nombre: valores.nombre,
          apellido: valores.apellido,
          telefono: valores.telefono || undefined,
          matricula: valores.matricula || undefined,
          cargo: valores.cargo || undefined,
          rol: valores.rol,
        });
        mostrar("Usuario creado.", "exito");
      }
      setModalAbierto(false);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo guardar el usuario.",
        "error",
      );
    } finally {
      setGuardando(false);
    }
  }

  async function confirmarCambioEstado() {
    if (!aCambiarEstado) return;
    setCambiandoEstado(true);
    try {
      const activarA = !aCambiarEstado.activo;
      await api.patch(`/usuarios/${aCambiarEstado.id}/estado`, {
        activo: activarA,
      });
      mostrar(activarA ? "Usuario reactivado." : "Usuario desactivado.", "exito");
      setACambiarEstado(null);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo cambiar el estado del usuario.",
        "error",
      );
    } finally {
      setCambiandoEstado(false);
    }
  }

  function abrirCambiarRol(u: Usuario) {
    setACambiarRol(u);
    setRolNuevo(u.rol);
  }

  async function confirmarCambioRol(e: FormEvent) {
    e.preventDefault();
    if (!aCambiarRol) return;
    setGuardandoRol(true);
    try {
      await api.patch(`/usuarios/${aCambiarRol.id}/rol`, { rol: rolNuevo });
      mostrar("Rol actualizado.", "exito");
      setACambiarRol(null);
      cargar();
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudo cambiar el rol.",
        "error",
      );
    } finally {
      setGuardandoRol(false);
    }
  }

  function abrirRestablecer(u: Usuario) {
    setARestablecer(u);
    setPasswordManual("");
    setPasswordTemporal(null);
  }

  async function confirmarRestablecer(e: FormEvent) {
    e.preventDefault();
    if (!aRestablecer) return;
    setRestableciendo(true);
    try {
      const datos = await api.post<{ password_temporal: string }>(
        `/usuarios/${aRestablecer.id}/restablecer-password`,
        passwordManual ? { password: passwordManual } : undefined,
      );
      setPasswordTemporal(datos.password_temporal);
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudo restablecer la contraseña.",
        "error",
      );
    } finally {
      setRestableciendo(false);
    }
  }

  async function abrirPermisos(u: Usuario) {
    setGestionandoPermisos(u);
    setCargandoPermisos(true);
    try {
      const datos = await api.get<{
        overrides: { permiso: Permiso; concedido: boolean }[];
      }>(`/usuarios/${u.id}`);
      const mapa = {} as Record<Permiso, EstadoOverride>;
      for (const p of CATALOGO_PERMISOS) mapa[p] = "default";
      for (const o of datos.overrides) {
        mapa[o.permiso] = o.concedido ? "conceder" : "denegar";
      }
      setEstadosPermisos(mapa);
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudieron cargar los permisos del usuario.",
        "error",
      );
      setGestionandoPermisos(null);
    } finally {
      setCargandoPermisos(false);
    }
  }

  async function guardarPermisos() {
    if (!gestionandoPermisos) return;
    setGuardandoPermisos(true);
    try {
      const overrides = CATALOGO_PERMISOS.filter(
        (p) => estadosPermisos[p] !== "default",
      ).map((p) => ({
        permiso: p,
        concedido: estadosPermisos[p] === "conceder",
      }));
      await api.patch(`/usuarios/${gestionandoPermisos.id}/permisos`, {
        overrides,
      });
      mostrar("Permisos actualizados.", "exito");
      setGestionandoPermisos(null);
    } catch (err) {
      mostrar(
        err instanceof ErrorApi
          ? err.message
          : "No se pudieron guardar los permisos.",
        "error",
      );
    } finally {
      setGuardandoPermisos(false);
    }
  }

  async function abrirAreas(u: Usuario) {
    setGestionandoAreas(u);
    setCargandoAreas(true);
    try {
      const datos = await api.get<{ areasPractica: string[] }>(`/usuarios/${u.id}`);
      setAreasSeleccionadas(new Set(datos.areasPractica));
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudieron cargar las unidades del usuario.",
        "error",
      );
      setGestionandoAreas(null);
    } finally {
      setCargandoAreas(false);
    }
  }

  function alternarArea(id: string) {
    setAreasSeleccionadas((prev) => {
      const nuevo = new Set(prev);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
  }

  async function guardarAreas() {
    if (!gestionandoAreas) return;
    setGuardandoAreas(true);
    try {
      await api.put(`/usuarios/${gestionandoAreas.id}/areas-practica`, {
        areaIds: [...areasSeleccionadas],
      });
      mostrar("Unidades de Negocio actualizadas.", "exito");
      setGestionandoAreas(null);
    } catch (err) {
      mostrar(
        err instanceof ErrorApi ? err.message : "No se pudieron guardar las unidades.",
        "error",
      );
    } finally {
      setGuardandoAreas(false);
    }
  }

  const columnas: ColumnaTabla<Usuario>[] = [
    {
      encabezado: "Nombre",
      render: (u) => (
        <span className="font-medium text-texto">{u.nombre_completo}</span>
      ),
    },
    { encabezado: "Email", render: (u) => u.email },
    {
      encabezado: "Rol",
      render: (u) => <Insignia texto={ETIQUETA_ROL[u.rol]} />,
    },
    {
      encabezado: "Estado",
      render: (u) => <Insignia texto={u.activo ? "Activo" : "Inactivo"} />,
    },
    {
      encabezado: "Último acceso",
      render: (u) => u.ultimo_acceso_en ?? "Nunca",
    },
    {
      encabezado: "Acciones",
      render: (u) => {
        const esPropio = u.id === usuarioActual?.id;
        return (
          <div className="flex flex-wrap gap-3">
            {permisos.has("usuarios.editar") && (
              <button
                onClick={() => abrirEditar(u)}
                className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              >
                Editar
              </button>
            )}
            {permisos.has("usuarios.cambiar_rol") && !esPropio && (
              <button
                onClick={() => abrirCambiarRol(u)}
                className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              >
                Cambiar rol
              </button>
            )}
            {permisos.has("permisos.administrar") && !esPropio && (
              <button
                onClick={() => abrirPermisos(u)}
                className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              >
                Permisos
              </button>
            )}
            {permisos.has("usuarios.editar") && areasDisponibles.length > 0 && (
              <button
                onClick={() => abrirAreas(u)}
                className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              >
                Unidades
              </button>
            )}
            {permisos.has("usuarios.restablecer_password") && !esPropio && (
              <button
                onClick={() => abrirRestablecer(u)}
                className="font-medium text-texto transition-colors hover:text-secundario hover:underline"
              >
                Restablecer contraseña
              </button>
            )}
            {!esPropio &&
              ((u.activo && permisos.has("usuarios.desactivar")) ||
                (!u.activo && permisos.has("usuarios.reactivar"))) && (
                <button
                  onClick={() => setACambiarEstado(u)}
                  className={`font-medium transition-colors hover:underline ${u.activo ? "text-error" : "text-exito"}`}
                >
                  {u.activo ? "Desactivar" : "Reactivar"}
                </button>
              )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <EncabezadoPagina
        titulo="Usuarios"
        descripcion="Empleados del estudio, roles y permisos"
        acciones={
          permisos.has("usuarios.crear") ? (
            <Boton onClick={abrirCrear}>+ Nuevo Usuario</Boton>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CampoBusqueda
          valor={busqueda}
          onCambio={setBusqueda}
          placeholder="Buscar por nombre o email..."
        />
        <Select
          value={filtroRol}
          onChange={(e) => setFiltroRol(e.target.value)}
          className="w-auto"
        >
          <option value="">Todos los roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ETIQUETA_ROL[r]}
            </option>
          ))}
        </Select>
        <Select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="w-auto"
        >
          <option value="">Todos los estados</option>
          <option value="activo">Activos</option>
          <option value="inactivo">Inactivos</option>
        </Select>
      </div>

      <Tabla
        columnas={columnas}
        filas={usuarios}
        claveFila={(u) => u.id}
        cargando={cargando}
        tituloVacio="No hay usuarios"
      />

      <Modal
        titulo={editando ? "Editar usuario" : "Nuevo usuario"}
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
      >
        <form onSubmit={manejarEnvio} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Nombre" obligatorio>
              <Input
                required
                value={valores.nombre}
                onChange={(e) =>
                  setValores((v) => ({ ...v, nombre: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="Apellido" obligatorio>
              <Input
                required
                value={valores.apellido}
                onChange={(e) =>
                  setValores((v) => ({ ...v, apellido: e.target.value }))
                }
              />
            </Campo>
          </div>
          <Campo etiqueta="Email" obligatorio>
            <Input
              type="email"
              required
              disabled={!!editando}
              value={valores.email}
              onChange={(e) =>
                setValores((v) => ({ ...v, email: e.target.value }))
              }
            />
          </Campo>
          {!editando && (
            <Campo etiqueta="Contraseña" obligatorio>
              <Input
                type="password"
                required
                value={valores.password}
                onChange={(e) =>
                  setValores((v) => ({ ...v, password: e.target.value }))
                }
              />
            </Campo>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Campo etiqueta="Teléfono">
              <Input
                value={valores.telefono}
                onChange={(e) =>
                  setValores((v) => ({ ...v, telefono: e.target.value }))
                }
              />
            </Campo>
            <Campo etiqueta="Matrícula">
              <Input
                value={valores.matricula}
                onChange={(e) =>
                  setValores((v) => ({ ...v, matricula: e.target.value }))
                }
              />
            </Campo>
          </div>
          <Campo etiqueta="Cargo">
            <Input
              value={valores.cargo}
              onChange={(e) =>
                setValores((v) => ({ ...v, cargo: e.target.value }))
              }
            />
          </Campo>
          {!editando && (
            <Campo etiqueta="Rol" obligatorio>
              <Select
                value={valores.rol}
                onChange={(e) =>
                  setValores((v) => ({ ...v, rol: e.target.value as Rol }))
                }
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ETIQUETA_ROL[r]}
                  </option>
                ))}
              </Select>
            </Campo>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setModalAbierto(false)}
              disabled={guardando}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardando}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <ConfirmacionDialog
        abierto={aCambiarEstado !== null}
        titulo={aCambiarEstado?.activo ? "Desactivar usuario" : "Reactivar usuario"}
        mensaje={
          aCambiarEstado?.activo
            ? `¿Desactivar a "${aCambiarEstado?.nombre_completo}"? No podrá volver a iniciar sesión.`
            : `¿Reactivar a "${aCambiarEstado?.nombre_completo}"? Podrá volver a iniciar sesión.`
        }
        cargando={cambiandoEstado}
        onConfirmar={confirmarCambioEstado}
        onCancelar={() => setACambiarEstado(null)}
        textoConfirmar={aCambiarEstado?.activo ? "Desactivar" : "Reactivar"}
        peligroso={aCambiarEstado?.activo}
      />

      <Modal
        titulo="Cambiar rol"
        abierto={aCambiarRol !== null}
        onCerrar={() => setACambiarRol(null)}
        ancho="max-w-sm"
      >
        <form onSubmit={confirmarCambioRol} className="space-y-4">
          <p className="text-sm text-texto-secundario">
            Rol de <strong>{aCambiarRol?.nombre_completo}</strong>:
          </p>
          <Campo etiqueta="Rol nuevo" obligatorio>
            <Select
              value={rolNuevo}
              onChange={(e) => setRolNuevo(e.target.value as Rol)}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ETIQUETA_ROL[r]}
                </option>
              ))}
            </Select>
          </Campo>
          <div className="flex justify-end gap-2 pt-2">
            <Boton
              type="button"
              variante="secundario"
              onClick={() => setACambiarRol(null)}
              disabled={guardandoRol}
            >
              Cancelar
            </Boton>
            <Boton type="submit" cargando={guardandoRol}>
              Guardar
            </Boton>
          </div>
        </form>
      </Modal>

      <Modal
        titulo="Restablecer contraseña"
        abierto={aRestablecer !== null}
        onCerrar={() => setARestablecer(null)}
        ancho="max-w-sm"
      >
        {passwordTemporal ? (
          <div className="space-y-4">
            <p className="text-sm text-texto-secundario">
              Contraseña temporal para{" "}
              <strong>{aRestablecer?.nombre_completo}</strong>. Comunicásela
              ahora — no va a poder volver a verse.
            </p>
            <div className="rounded-lg border border-borde bg-fondo px-3 py-2 font-mono text-sm text-texto">
              {passwordTemporal}
            </div>
            <p className="text-xs text-texto-secundario">
              El usuario deberá cambiarla en su próximo inicio de sesión y sus
              sesiones anteriores quedaron invalidadas.
            </p>
            <div className="flex justify-end pt-2">
              <Boton onClick={() => setARestablecer(null)}>Cerrar</Boton>
            </div>
          </div>
        ) : (
          <form onSubmit={confirmarRestablecer} className="space-y-4">
            <p className="text-sm text-texto-secundario">
              Se generará una contraseña temporal para{" "}
              <strong>{aRestablecer?.nombre_completo}</strong> y se cerrarán
              sus sesiones activas.
            </p>
            <Campo etiqueta="Contraseña manual (opcional)">
              <Input
                type="text"
                value={passwordManual}
                onChange={(e) => setPasswordManual(e.target.value)}
                placeholder="Dejar en blanco para generarla automáticamente"
              />
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Boton
                type="button"
                variante="secundario"
                onClick={() => setARestablecer(null)}
                disabled={restableciendo}
              >
                Cancelar
              </Boton>
              <Boton type="submit" cargando={restableciendo}>
                Restablecer
              </Boton>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        titulo="Permisos personalizados"
        abierto={gestionandoPermisos !== null}
        onCerrar={() => setGestionandoPermisos(null)}
        ancho="max-w-3xl"
      >
        {cargandoPermisos || !gestionandoPermisos ? (
          <p className="text-sm text-texto-secundario">Cargando...</p>
        ) : (
          <div className="space-y-6">
            <p className="text-sm text-texto-secundario">
              Permisos de <strong>{gestionandoPermisos.nombre_completo}</strong>{" "}
              ({ETIQUETA_ROL[gestionandoPermisos.rol]}). "Por defecto" respeta
              lo que otorga el rol; "Conceder" agrega una excepción puntual;
              "Denegar" quita el permiso aunque el rol lo incluya.
            </p>
            {GRUPOS_PERMISOS.map(({ grupo, etiqueta }) => {
              const permisosGrupo = CATALOGO_PERMISOS.filter((p) =>
                p.startsWith(`${grupo}.`),
              );
              if (permisosGrupo.length === 0) return null;
              return (
                <div key={grupo}>
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-texto-secundario">
                    {etiqueta}
                  </h3>
                  <div className="divide-y divide-borde rounded-lg border border-borde">
                    {permisosGrupo.map((p) => {
                      const heredado = PERMISOS_POR_ROL[
                        gestionandoPermisos.rol
                      ].includes(p);
                      const estado = estadosPermisos[p] ?? "default";
                      const efectivo =
                        estado === "conceder" ||
                        (estado === "default" && heredado);
                      return (
                        <div
                          key={p}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-texto">
                              {ETIQUETAS_PERMISOS[p]}
                            </span>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                efectivo ? "bg-exito/10 text-exito" : "bg-fondo text-texto-secundario"
                              }`}
                            >
                              {efectivo ? "Concedido" : "Sin acceso"}
                            </span>
                            {heredado && (
                              <span className="text-xs text-texto-secundario">
                                (del rol)
                              </span>
                            )}
                          </div>
                          <Select
                            value={estado}
                            onChange={(e) =>
                              setEstadosPermisos((s) => ({
                                ...s,
                                [p]: e.target.value as EstadoOverride,
                              }))
                            }
                            className="w-auto"
                          >
                            <option value="default">Por defecto</option>
                            <option value="conceder">Conceder</option>
                            <option value="denegar">Denegar</option>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end gap-2 pt-2">
              <Boton
                variante="secundario"
                onClick={() => setGestionandoPermisos(null)}
                disabled={guardandoPermisos}
              >
                Cancelar
              </Boton>
              <Boton onClick={guardarPermisos} cargando={guardandoPermisos}>
                Guardar permisos
              </Boton>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        titulo="Unidades de Negocio"
        abierto={gestionandoAreas !== null}
        onCerrar={() => setGestionandoAreas(null)}
        ancho="max-w-lg"
      >
        {cargandoAreas || !gestionandoAreas ? (
          <p className="text-sm text-texto-secundario">Cargando...</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-texto-secundario">
              Unidades de Negocio de <strong>{gestionandoAreas.nombre_completo}</strong>. Si no
              tiene ninguna unidad asignada, no verá expedientes de ninguna unidad en particular
              — sólo lo que sea general del estudio (sin unidad asignada).
            </p>
            <SelectorMultiple
              opciones={areasDisponibles.map((a) => ({ id: a.id, etiqueta: a.nombre }))}
              seleccionados={areasSeleccionadas}
              onCambiar={alternarArea}
              vacio="No hay Unidades de Negocio configuradas en el estudio."
            />
            <div className="flex justify-end gap-2 pt-2">
              <Boton
                variante="secundario"
                onClick={() => setGestionandoAreas(null)}
                disabled={guardandoAreas}
              >
                Cancelar
              </Boton>
              <Boton onClick={guardarAreas} cargando={guardandoAreas}>
                Guardar unidades
              </Boton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
